import { createContext, useContext, useState } from 'react';
import { ExperienceDetailService } from 'core/api/services/experienceDetail';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ExperienceDetailContext = createContext(null);

export const ExperienceDetailProvider = ({ children }) => {
    const { t } = useTranslation();
    const translate = (value) => t(`experience_detail_form.${value}`);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [experienceName, setExperienceName] = useState(null);
    const [experienceDetails, setExperienceDetails] = useState([]);
    const [total, setTotal] = useState(0);

    const getExperienceDetails = async (experienceId, options) => {
        try {
            setLoading(true);
            const result = await ExperienceDetailService.get(experienceId, options);

            if (!result) {
                throw new Error('Error get experience details');
            }

            setExperienceName(result?.experienceName);
            setExperienceDetails(result?.experienceDetails);
            setTotal(result?.total);
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const updateExperienceDetail = async (experienceId, experienceDetailId, values) => {
        try {
            setSubmitting(true);
            const result = await ExperienceDetailService.update(experienceId, experienceDetailId, values);

            if (!result?.id) {
                throw new Error(translate('toasts.errors.update_failed'));
            }

            toast.success(translate('toasts.success.updated'), {
                position: 'top-right',
                theme: 'colored',
            });

            setExperienceDetails((prev) => prev.map((exp) => (exp?.id === result.id ? result : exp)));
            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const createExperienceDetail = async (experienceId, values) => {
        try {
            setSubmitting(true);
            const result = await ExperienceDetailService.create(experienceId, values);

            if (!result?.id) {
                throw new Error(translate('toasts.errors.create_failed'));
            }

            toast.success(translate('toasts.success.created'), {
                position: 'top-right',
                theme: 'colored',
            });

            setExperienceDetails([...experienceDetails, result]);
            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ExperienceDetailContext.Provider
            value={{
                loading,
                submitting,
                experienceName,
                experienceDetails,
                total,
                translate,
                getExperienceDetails,
                updateExperienceDetail,
                createExperienceDetail,
            }}
        >
            {children}
        </ExperienceDetailContext.Provider>
    );
};

ExperienceDetailProvider.displayName = 'ExperienceDetailProvider';

export const useExperienceDetail = () => useContext(ExperienceDetailContext);
